import _ from "lodash";
import { Checkbox, Form, Header, Input, Tab } from "semantic-ui-react";

import pollsCss from "../../assets/styles/polls/polls.module.scss";
import gifImg from "../../assets/stylesheets/static/media/gif.png";
import standardImg from "../../assets/stylesheets/static/media/mcq.png";
import yesNoText from "../../assets/stylesheets/static/media/yes-no-text.png";
import emoji from "../../assets/stylesheets/static/media/emoji.png";
import { PollCustomization } from "./PollCustomization";
import { PollsSetting } from "./PollsSetting";
import EmojiComponent from "./specs/EmojiSelector";
import { useDispatch } from "react-redux";
import {
  MAX_NUMBER_LIMIT,
  MIN_NUMBER_LIMIT,
} from "../../constants/pollConstant";

export const PollsEditor = ({ draft, setDraft }) => {
  const dispatch = useDispatch();
  const sizeOptions = [
    { text: "Multiple Choice", value: "multiple-choice" },
    {
      text: "Yes/No",
      value: "yes-no",
    },
    {
      text: "Scale",
      value: "rating",
    },
  ];

  const handleDraftChange = (key, value) => {
    setDraft((prev) => {
      const newDraft = _.cloneDeep(prev);

      _.set(newDraft, key, value);

      const setPollOptions = (options) => {
        const templateDataKeys = {
          option_text: "",
          gif_url: "",
          emoji: "👍",
          poll_type_variant: newDraft?.data?.poll_type,
        };

        if (prev?.id !== "new") {
          const filteredOptions = prev?.data?.poll_options?.filter(
            (option) => !(option.archived && !option.id)
          );

          newDraft.data.poll_options = [
            ...filteredOptions.map((option) => ({
              ...option,
              archived: true,
            })),
            ...options.map((option) => ({
              archived: false,
              option_template_data: {
                ...templateDataKeys,
                ...option,
              },
            })),
          ];
        } else {
          newDraft.data.poll_options = options.map((option) => ({
            archived: false,
            option_template_data: {
              ...templateDataKeys,
              ...option,
            },
          }));
        }
      };

      if (key === "data.poll_type") {
        switch (value) {
          case "multiple-choice":
            setPollOptions([
              { option_text: "", poll_type_variant: "multiple-choice" },
            ]);
            newDraft.data.template_data.variant = "text";
            break;
          case "yes-no":
            if (prev?.data?.template_data?.variant === "yesNoText") {
              setPollOptions([
                { option_text: "Yes", emoji: "", poll_type_variant: "yes-no" },
                { option_text: "No", emoji: "", poll_type_variant: "yes-no" },
              ]);
            } else if (prev?.data?.template_data?.variant === "emoji") {
              setPollOptions([
                {
                  option_text: "Yes",
                  emoji: "👍",
                  poll_type_variant: "yes-no",
                },
                { option_text: "No", emoji: "👎", poll_type_variant: "yes-no" },
              ]);
            }
            break;
          case "rating":
            setPollOptions(
              Array.from(
                {
                  length:
                    prev?.data?.template_data?.ratingScale?.max -
                    prev?.data?.template_data?.ratingScale?.min +
                    1,
                },
                (_, i) => ({
                  option_text: (
                    prev?.data?.template_data?.ratingScale?.min + i
                  ).toString(),
                  emoji: "👍",
                  poll_type_variant: "rating",
                })
              )
            );

            break;
          default:
            break;
        }
      }

      if (
        key === "data.template_data.variant" &&
        ["text", "gif", "yesNoText", "emoji"].includes(value)
      ) {
        if (value === "yesNoText") {
          setPollOptions([
            { option_text: "Yes", emoji: "", poll_type_variant: "yes-no" },
            { option_text: "No", emoji: "", poll_type_variant: "yes-no" },
          ]);
        } else if (value === "emoji") {
          setPollOptions([
            { option_text: "Yes", emoji: "👍", poll_type_variant: "yes-no" },
            { option_text: "No", emoji: "👎", poll_type_variant: "yes-no" },
          ]);
        } else if (value === "gif" || value === "text") {
          const existingOptions = prev?.data?.poll_options?.filter(
            (option) => !option.archived
          ) || [];
          
          if (existingOptions.length > 0) {
            setPollOptions(existingOptions.map(option => ({
              option_text: option.option_template_data.option_text || "",
              poll_type_variant: "multiple-choice"
            })));
          } else {
            setPollOptions([{ option_text: "Option" }]);
          }
        } else {
          setPollOptions([{ option_text: "" }]);
        }
      }

      return newDraft;
    });
  };

  const variantCard = (value, label, name, image, content) => {
    return (
      <div style={{ width: "100%", height: "100%" }}>
        <div
          className={pollsCss.variantCard}
          onClick={() => {
            handleDraftChange("data.template_data.variant", value);
          }}
        >
          <img src={image} alt="GIF Variant" />
        </div>
        <Form.Radio
          label={label}
          name={name}
          value={value}
          checked={draft?.data?.template_data?.variant === value}
          onChange={() => {
            handleDraftChange("data.template_data.variant", value);
          }}
        />
        <p className={pollsCss?.infoText}>{content}</p>
      </div>
    );
  };

  const renderMcqVariants = () => {
    const standardContent =
      "text multi-choice poll with up to 5 answer options";
    const gifContent = "Use images or memes as answer options instead of text.";
    return (
      <div className={pollsCss.variantType}>
        {variantCard(
          "text",
          "Standard",
          "multiple-choice",
          standardImg,
          standardContent
        )}
        {variantCard("gif", "GIF", "multiple-choice", gifImg, gifContent)}
      </div>
    );
  };

  const renderYesNoVariants = () => {
    return (
      <>
        <div className={pollsCss.variantType}>
          {variantCard("yesNoText", "Yes/No", "yes-no", yesNoText)}
          {variantCard("emoji", "emoji", "yes-no", emoji)}
        </div>
        <p className={pollsCss?.infoText}>
          This poll type offers only two options: "Yes" or "No." You can also
          enhance engagement by incorporating emojis for each choice
        </p>
      </>
    );
  };

  const renderScallingInputs = () => {
    return (
      <div>
        <Header as="h4" className={pollsCss.lableHeading}>
          Scale range
        </Header>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <Header as="h4" className={pollsCss.labels}>
              Min
            </Header>
            <Input
              placeholder="Min"
              type="number"
              onKeyDown={(evt) =>{
                if (["e", "E", "+", "-"].includes(evt.key)) {
                  evt.preventDefault();
                }
                if (evt.key === "Enter") {
                  evt.currentTarget.blur(); 
                }
              }
              }
              style={{ marginRight: "10px" }}
              onChange={(e) => {
                const value = e.target.value.trim();
                const parsedValue = value === "" ? "" : Number.parseInt(value, 10);
                handleDraftChange("data.template_data.ratingScale.min", parsedValue);
              }} 
           
              onBlur={(e) => {
                const value = e.target.value.trim();
                let parsedValue =
                  value === "" ? null : Number.parseInt(value, 10);
                if (parsedValue > 10) {
                  dispatch({ type: MAX_NUMBER_LIMIT, parsedValue });
                  parsedValue = 10;
                }
                if (parsedValue < 1) {
                  dispatch({ type: MIN_NUMBER_LIMIT, parsedValue });
                  parsedValue = 1;
                }

                handleDraftChange(
                  "data.template_data.ratingScale.min",
                  parsedValue
                );

                const max = draft.data.template_data.ratingScale?.max;
                if (
                  parsedValue !== null &&
                  max !== undefined &&
                  parsedValue <= max
                ) {
                  const updatedPollOptions = [...draft.data.poll_options];

                  // Add missing options if necessary
                  for (let i = parsedValue; i <= max; i++) {
                    const existingOption = updatedPollOptions.find(
                      (option) =>
                        option.option_template_data.option_text === i.toString()
                    );

                    if (!existingOption) {
                      updatedPollOptions.push({
                        archived: false,
                        option_template_data: {
                          option_text: i.toString(),
                          gif_url: "",
                          emoji: "👍",
                          poll_type_variant: "rating",
                        },
                      });
                    }
                  }

                  updatedPollOptions.forEach((option) => {
                    const optionValue = parseInt(
                      option.option_template_data.option_text,
                      10
                    );

                    if (optionValue >= parsedValue && optionValue <= max) {
                      option.archived = false;
                    } else {
                      option.archived = true;
                    }
                  });

                  handleDraftChange("data.poll_options", updatedPollOptions);
                }
              }}
              value={draft.data.template_data.ratingScale?.min || ""}
            />
          </div>
          <div>
            <Header as="h4" className={pollsCss.labels}>
              Max
            </Header>
            <Input
              placeholder="Max"
              type="number"
              onKeyDown={(evt) =>{
                if (["e", "E", "+", "-"].includes(evt.key)) {
                  evt.preventDefault();
                }
                if (evt.key === "Enter") {
                  evt.currentTarget.blur(); 
                }
              }
              }
              onChange={(e) => {
                const value = e.target.value.trim();
                const parsedValue = value === "" ? "" : Number.parseInt(value, 10);
                handleDraftChange("data.template_data.ratingScale.max", parsedValue);
              }}
              onBlur={(e) => {
                const value = e.target.value.trim();
                let parsedValue =
                  value === "" ? null : Number.parseInt(value, 10);
                if (parsedValue > 10) {
                  dispatch({ type: MAX_NUMBER_LIMIT, parsedValue });
                  parsedValue = 10;
                }

                if (parsedValue < 1) {
                  dispatch({ type: MIN_NUMBER_LIMIT, parsedValue });
                  parsedValue = 1;
                }

                handleDraftChange(
                  "data.template_data.ratingScale.max",
                  parsedValue
                );

                const min = draft.data.template_data.ratingScale?.min;
                if (
                  parsedValue !== null &&
                  min !== undefined &&
                  min <= parsedValue
                ) {
                  const updatedPollOptions = [...draft.data.poll_options];
                  for (let i = min; i <= parsedValue; i++) {
                    const existingOption = updatedPollOptions.find(
                      (option) =>
                        option.option_template_data.option_text === i.toString()
                    );

                    if (!existingOption) {
                      console.log(existingOption, existingOption);
                      updatedPollOptions.push({
                        archived: false,
                        option_template_data: {
                          option_text: i.toString(),
                          gif_url: "",
                          emoji: "👍",
                          poll_type_variant: "rating",
                        },
                      });
                    }
                  }

                  updatedPollOptions.forEach((option) => {
                    const optionValue = parseInt(
                      option.option_template_data.option_text,
                      10
                    );
                    if (optionValue >= min && optionValue <= parsedValue) {
                      option.archived = false;
                    } else {
                      option.archived = true;
                    }
                  });

                  handleDraftChange("data.poll_options", updatedPollOptions);
                }
              }}
              value={draft.data.template_data.ratingScale?.max || ""}
            />
          </div>
        </div>
        <div style={{ marginTop: "15px" }}>
          <Checkbox
            toggle
            label={"Translate values to emojis"}
            onChange={() => {
              handleDraftChange(
                "data.template_data.translateToEmojis",
                !draft.data.template_data.translateToEmojis
              );
            }}
            checked={draft.data.template_data.translateToEmojis}
          />
        </div>

        {draft?.data?.poll_type === "rating" &&
          draft.data.template_data.translateToEmojis && (
            <EmojiComponent
              setDraft={setDraft}
              draft={draft}
              handleDraftChange={handleDraftChange}
            />
          )}
      </div>
    );
  };

  const renderPollType = () => {
    return (
      <>
        <Header as="h4" className={pollsCss.labels}>
          Poll type
        </Header>
        <Form.Dropdown
          fluid
          selection
          options={sizeOptions}
          placeholder="Choose Size..."
          onChange={(e, { value }) => {
            handleDraftChange("data.poll_type", value);
            if (value === "yes-no") {
              handleDraftChange("data.template_data.variant", "yesNoText");
            }
          }}
          value={draft?.data?.poll_type}
        />
        {draft.data.poll_type !== "rating" && (
          <Header as="h4" className={pollsCss.labels}>
            Variant
          </Header>
        )}
        {draft?.data?.poll_type === "multiple-choice" && renderMcqVariants()}
        {draft?.data?.poll_type === "yes-no" && renderYesNoVariants()}
        {draft.data.poll_type === "rating" && renderScallingInputs()}
      </>
    );
  };

  const renderTabs = () => {
    const tables = [
      { menuItem: "Customize", render: renderTable("customize") },
      { menuItem: "Settings", render: renderTable("settings") },
    ];

    return <Tab menu={{ secondary: true, pointing: true }} panes={tables} />;
  };

  const renderTable = (status) => () =>
    (
      <Tab.Pane as="div" attached="false">
        {status === "customize" && (
          <PollCustomization
            draft={draft}
            setDraft={setDraft}
            handleDraftChange={handleDraftChange}
          />
        )}
        {status === "settings" && (
          <PollsSetting
            draft={draft}
            setDraft={setDraft}
            handleDraftChange={handleDraftChange}
          />
        )}
      </Tab.Pane>
    );

  return (
    <div>
      <Header as="h1" size="large">
        Poll
      </Header>
      {renderPollType()}
      {renderTabs()}
    </div>
  );
};
